/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/ui/badge.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type PayrollRosterRow } from '../../lib/api/payroll.js';
import { usePayrollRoster } from '../../lib/queries/payroll.js';
import { initialsOf } from '../../lib/utils/initials.js';
import { formatInr } from '../../lib/utils/money.js';

type Tab = 'all' | 'on_roll' | 'consultant';
const TABS: readonly Tab[] = ['all', 'on_roll', 'consultant'];

/** Rate label: on-roll → ₹/mo · consultant → ₹/session (Figma). */
function rateLabel(row: PayrollRosterRow, t: ReturnType<typeof useTranslation>['t']): string {
  if (row.category === 'consultant') {
    return row.perSessionRatePaise !== null
      ? t('payroll.perSession', { amount: formatInr(row.perSessionRatePaise) })
      : '—';
  }
  return row.monthlyFixedPaise !== null
    ? t('payroll.perMonth', { amount: formatInr(row.monthlyFixedPaise) })
    : '—';
}

function RosterCard({ row }: { readonly row: PayrollRosterRow }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--accent)] text-sm font-bold text-white">
          {initialsOf(row.therapistName)}
        </span>
        <div>
          <p className="font-semibold text-slate-800 dark:text-white">{row.therapistName}</p>
          <p className="text-sm text-slate-400">
            {t(`payroll.category_${row.category}`)}
            {row.specialisation !== null ? ` · ${row.specialisation}` : ''}
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className="font-semibold text-slate-800 dark:text-white">{rateLabel(row, t)}</p>
        <p className="text-xs text-slate-400">
          {t('payroll.sessionsCount', { count: row.sessionsCount })}
        </p>
      </div>
      <Badge
        label={t(`payroll.status_${row.status}`)}
        tone={row.status === 'paid' ? 'green' : 'amber'}
      />
    </div>
  );
}

/** Finance → Payroll roster (Figma): All/On-Roll/Consultant tabs + per-therapist cards. */
export function PayrollRoster(): JSX.Element {
  const { t } = useTranslation();
  const roster = usePayrollRoster();
  const [tab, setTab] = useState<Tab>('all');
  const select = useCallback(
    (next: Tab) => () => {
      setTab(next);
    },
    [],
  );
  const all = roster.data ?? [];
  const rows = tab === 'all' ? all : all.filter((r) => r.category === tab);

  return (
    <div>
      <div className="mb-4 inline-flex rounded-xl bg-slate-100 p-1 dark:bg-slate-800">
        {TABS.map((value) => (
          <button
            key={value}
            type="button"
            onClick={select(value)}
            className={`focus-ring rounded-lg px-5 py-2 text-sm font-medium ${
              tab === value ? 'bg-[var(--accent)] text-white' : 'text-slate-600 dark:text-slate-300'
            }`}
          >
            {t(`payroll.tab_${value}`)}
          </button>
        ))}
      </div>
      <QueryState
        isLoading={roster.isLoading}
        isError={roster.isError}
        isEmpty={rows.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('payroll.rosterEmpty')}
      >
        <div className="space-y-3">
          {rows.map((row) => (
            <RosterCard key={row.therapistId} row={row} />
          ))}
        </div>
      </QueryState>
    </div>
  );
}
